import dayjs from "dayjs";
import type {
    Citation, Message, MessagePart, ConversationSummary, ImagePartData, MessageImage, StepRecord,
    AgentPartData, QuestionData, QuestionOption, ActionResultStatus,
} from "./types";
import { normalizeSteps } from "./utils/stepUtils";

export const INTRO_MESSAGE = {
    id: 1,
    role: "assistant",
    content: "Hi there! Ask me a question!",
    type: "text",
    created: 0,
    metadata: {},
};

// The sidebar's page size. The refresh after each turn must ask for the same first page.
export const CONVERSATION_PAGE_SIZE = 50;

export const FALLBACK_ERROR_MESSAGE = "Sorry, I didn't understand that. Please try again.";

export const AGENT_MODEL_OPTIONS = [
    { label: "Backend default", value: "" },
    { label: "Kimi (Moonshot)", value: "moonshot:kimi" },
    { label: "Haiku", value: "anthropic:haiku" },
    { label: "Sonnet", value: "anthropic:sonnet" },
    { label: "Opus", value: "anthropic:opus" },
    { label: "GPT-4o Nano", value: "openai:nano" },
];

export const isAIMessage = (message: { role: string }) => message.role === "assistant";

export const formatCitation = (c: Citation): string =>
    c.page != null ? `${c.title}, ${c.year}, p. ${c.page}` : `${c.title}, ${c.year}`;

// QueueRender still emits "stat_card" as its viz_type hint — keep for skeleton display.
// "insights" is set by the insights_generating stream event.
export type ArtifactVizType = "table" | "chart" | "stat_card" | "insights";

// "stat" matches the type field on rendered MessagePart artifacts from the backend
export const ARTIFACT_VIZ_TYPES = new Set<string>(["table", "chart", "stat"]);

// ---------- WhistleAgent parts: the gate ----------

export const AGENT_PART_TYPES = new Set<string>(["action_card", "question", "action_result"]);

const KNOWN_PART_TYPES = new Set<string>([
    "text", "table", "chart", "stat", "insights", "summary", "summary_v2", ...AGENT_PART_TYPES,
]);

const HEX_ID = /^[0-9a-f]{32}$/;
const isHexId = (v: unknown): v is string => typeof v === "string" && HEX_ID.test(v);
const isLabel = (v: unknown): v is string => typeof v === "string" && v.length >= 1 && v.length <= 60;
const obj = (v: unknown): Record<string, unknown> | null =>
    v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const parseOptions = (raw: unknown): QuestionOption[] | null => {
    if (!Array.isArray(raw) || raw.length < 2 || raw.length > 6) return null;
    const options: QuestionOption[] = [];
    for (const o of raw) {
        const r = obj(o);
        if (!r || !isLabel(r.id) || !isLabel(r.label)) return null;
        options.push({ id: r.id, label: r.label });
    }
    const unique = (key: "id" | "label") => new Set(options.map(o => o[key])).size === options.length;
    return unique("id") && unique("label") ? options : null;
};

const parseQuestion = (raw: unknown): QuestionData | null => {
    const q = obj(raw);
    if (!q || !isHexId(q.question_id)) return null;
    if (typeof q.text !== "string" || !q.text) return null;
    const answerType = q.answer_type;
    if (answerType !== "choice" && answerType !== "multi_choice" && answerType !== "text") return null;
    if (answerType === "text") {
        if (q.options != null && !(Array.isArray(q.options) && q.options.length === 0)) return null;
        return { question_id: q.question_id, text: q.text, answer_type: answerType, options: [] };
    }
    const options = parseOptions(q.options);
    return options ? { question_id: q.question_id, text: q.text, answer_type: answerType, options } : null;
};

const RESULT_STATUSES = new Set<string>(["saved", "refused", "failed", "cancelled"]);

// Checks the contract only. It corrects nothing: a part that fails one check is dropped whole.
// Approved exception to the 10-branch rule: one flat list of checks, one per contract field.
export const parseAgentPart = (part: unknown): AgentPartData | null => {
    const p = obj(part);
    const d = p ? obj(p.data) : null;
    if (!p || !d) return null;
    if (p.type === "action_card") {
        if (!isHexId(d.proposal_id)) return null;
        const rows = d.rows;
        if (!Array.isArray(rows) || rows.length > 8 || !rows.every(r => typeof r === "string" && r.length <= 120)) return null;
        const question = parseQuestion(d.question);
        if (!question || question.answer_type !== "choice" || question.question_id !== d.proposal_id) return null;
        return { proposal_id: d.proposal_id, rows: rows as string[], question };
    }
    if (p.type === "question") return parseQuestion(d);
    if (p.type === "action_result") {
        if (!isHexId(d.proposal_id) || typeof d.status !== "string" || !RESULT_STATUSES.has(d.status)) return null;
        if (typeof p.title !== "string" || typeof d.message !== "string") return null;
        return { proposal_id: d.proposal_id, status: d.status as ActionResultStatus, message: d.message };
    }
    return null;
};

export type PartClass = "draw" | "copy" | "unknown" | "malformed";

// `prevDrawnId` is the id of the agent part directly before this one, when the reader drew it.
// The backend sends the text copy of an agent part directly after it, so one id is enough. A
// copy of a part the reader did not draw stays visible, so the coach can still answer.
export const classifyPart = (part: unknown, prevDrawnId: string | null): PartClass => {
    const p = obj(part);
    const type = typeof p?.type === "string" ? p.type : "";
    if (type === "text" && prevDrawnId !== null && obj(p?.data)?.fallback_for === prevDrawnId) return "copy";
    if (!KNOWN_PART_TYPES.has(type)) return "unknown";
    if (AGENT_PART_TYPES.has(type) && parseAgentPart(part) === null) return "malformed";
    return "draw";
};

export const agentPartId = (data: AgentPartData): string =>
    "question_id" in data ? data.question_id : data.proposal_id;

// Each question id → its latest card or question row. Result rows never go in: a `failed`
// result carries the card's id, and keying it would make the card look old and kill its retry.
export const latestAgentRowById = (messages: Message[]): Map<string, Message> => {
    const map = new Map<string, Message>();
    for (const m of messages) {
        if (m.type !== "action_card" && m.type !== "question") continue;
        const data = m.metadata.data as AgentPartData | null | undefined;
        if (data) map.set(agentPartId(data), m);
    }
    return map;
};

// Each card or question row → the option ids a click picked for it. A reply goes to the row of
// its id that was latest when the reply was sent, so a re-asked question keeps each pick apart.
export const pickedChoicesByRow = (messages: Message[]): Map<Message["id"], string[]> => {
    const rowById = new Map<string, Message>();
    const picks = new Map<Message["id"], string[]>();
    for (const m of messages) {
        const data = m.metadata.data as AgentPartData | null | undefined;
        if ((m.type === "action_card" || m.type === "question") && data) rowById.set(agentPartId(data), m);
        const reply = m.metadata.reply;
        const row = reply && rowById.get(reply.questionId);
        if (reply && row) picks.set(row.id, reply.choices);
    }
    return picks;
};

// ---------- Conversation history helpers ----------

interface StoredMessage {
    id: string | null;
    version: "2.0" | null;
    role: "user" | "assistant";
    turn_index: number;
    created: number;        // Unix seconds
    parts: MessagePart[];
    // reply_click: {question_id, choices} when the user message before this one had reply_to.
    trace: { open?: unknown; reply_click?: unknown } | null;
    memory: object;         // ignored
    steps?: StepRecord[];   // absent on messages written before step records, and in the sandbox
}

export type StoredProblem = { kind: "malformed" | "unknown" | "open" | "reply_click"; part?: unknown };

interface MappedConversation {
    messages: Message[];
    stepsByTurn: Record<string, StepRecord[]>;
    openQuestionIds: string[];
    problems: StoredProblem[];
}

// One stored assistant part → at most one row. Pure: the caller reports the problem.
const mapAssistantPart = (
    part: MessagePart, partIndex: number, msgId: string, created: number, prevDrawnId: string | null,
): { row: Message | null; drawnId: string | null; problem?: StoredProblem } => {
    const cls = classifyPart(part, prevDrawnId);
    if (cls === "copy") return { row: null, drawnId: null };
    if (cls !== "draw") return { row: null, drawnId: null, problem: { kind: cls, part } };
    const id = `${msgId}-p${partIndex}`;
    if (part.type === "text") {
        if (!part.content) return { row: null, drawnId: null };
        return {
            row: { id, role: "assistant", type: "text", content: part.content, created, metadata: { citations: part.citations ?? [], turnId: msgId } },
            drawnId: null,
        };
    }
    if (AGENT_PART_TYPES.has(part.type)) {
        // content is kept for MessageReview, which reads it first. The UI does not draw it.
        const data = parseAgentPart(part) as AgentPartData;
        return {
            row: { id, role: "assistant", type: part.type, content: part.content, created, metadata: { data, title: part.title, turnId: msgId } },
            drawnId: agentPartId(data),
        };
    }
    // "summary_v2" is the persisted part type for what the FE renders as "summary".
    const type = part.type === "summary_v2" ? "summary" : part.type;
    return {
        row: {
            id, role: "assistant", type, content: "", created,
            // Citations only ever belong to the summary part, never table/chart/stat.
            metadata: { data: part.data, title: part.title, turnId: msgId, ...(type === "summary" && { citations: part.citations ?? [] }) },
        },
        drawnId: null,
    };
};

// The `open` list of a turn_done frame or a stored trace. null when it is present but malformed.
export const parseOpenList = (open: unknown): string[] | null =>
    Array.isArray(open) && open.every(isHexId) ? (open as string[]) : null;

const readOpen = (trace: StoredMessage["trace"]): { open: string[]; problem?: StoredProblem } => {
    if (trace == null || trace.open === undefined) return { open: [] };
    const open = parseOpenList(trace.open);
    return open ? { open } : { open: [], problem: { kind: "open", part: trace.open } };
};

// A typed answer has choices [] and gives no reply: its question shows no pick.
const readReplyClick = (trace: StoredMessage["trace"]): { reply?: { questionId: string; choices: string[] }; problem?: StoredProblem } => {
    if (trace?.reply_click == null) return {};
    const r = obj(trace.reply_click);
    if (!r || !isHexId(r.question_id) || !Array.isArray(r.choices) || !r.choices.every(isLabel)) {
        return { problem: { kind: "reply_click", part: trace.reply_click } };
    }
    return r.choices.length ? { reply: { questionId: r.question_id, choices: r.choices as string[] } } : {};
};

// Returns the step records beside the rows, keyed by the stored message id. That id is the same
// string the live stream sends as message_id, so a reloaded turn and a live one share a key.
export const mapStoredMessages = (backendMessages: StoredMessage[]): MappedConversation => {
    const result: Message[] = [];
    const stepsByTurn: Record<string, StepRecord[]> = {};
    const problems: StoredProblem[] = [];
    let openQuestionIds: string[] = [];
    for (const [index, msg] of backendMessages.entries()) {
        const created = msg.created * 1000;
        const msgId = msg.id ?? `stored-${index}`;
        const parts = msg.parts ?? [];

        // Assumption (per backend contract): only user turns ever carry "image" parts — the LLM
        // doesn't generate images. If that changes, an "image" part on an assistant message would
        // fall through the generic branch below and render as an empty bubble; it'd need its own
        // handling here and in MessageList's ChatImage rendering.
        if (msg.role === "user") {
            const content = parts
                .filter(p => p.type === "text" && p.content)
                .map(p => p.content)
                .join("\n");
            const images: MessageImage[] = parts
                .filter(p => p.type === "image" && p.data)
                .map(p => ({ ref: (p.data as unknown as ImagePartData).ref }));
            if (content || images.length) {
                result.push({
                    id: msgId, role: "user", type: "text", content, created,
                    metadata: images.length ? { images } : {},
                });
            }
            continue;
        }

        // The anchor row the accordion renders on. It goes before the part rows so the steps
        // always sit above the answer, and it exists even for a turn that produced no parts.
        // Coerce before storing: these records came straight off the GET, and the renderer
        // reads data.x without guards. An unusable record is dropped rather than rendered blank,
        // and a turn left with none gets no anchor row at all.
        const steps = normalizeSteps(msg.steps);
        if (steps.length) {
            if (msg.id) {
                stepsByTurn[msg.id] = steps;
                result.push({
                    id: `${msgId}-steps`, role: "assistant", type: "steps", content: "", created,
                    metadata: { turnId: msgId },
                });
            } else {
                console.warn("[Chatbot] stored assistant message has steps but no id", index);
            }
        }

        let prevDrawnId: string | null = null;
        for (const [partIndex, part] of parts.entries()) {
            const { row, drawnId, problem } = mapAssistantPart(part, partIndex, msgId, created, prevDrawnId);
            prevDrawnId = drawnId;
            if (row) result.push(row);
            if (problem) problems.push(problem);
        }

        // The latest assistant message owns the open list. Older messages are overwritten.
        const { open, problem } = readOpen(msg.trace);
        openQuestionIds = open;
        if (problem) problems.push(problem);

        // The backend stores the click on the reply turn. It belongs to the user row of that turn.
        const click = readReplyClick(msg.trace);
        const userRow = click.reply && result.findLast(m => m.role === "user");
        if (userRow) userRow.metadata.reply = click.reply;
        if (click.problem) problems.push(click.problem);
    }
    return { messages: result, stepsByTurn, openQuestionIds, problems };
};

if (import.meta.env?.DEV) {
    const ID = "a".repeat(32);
    const ID2 = "b".repeat(32);
    const card = (rows: string[]): MessagePart => ({
        type: "action_card", title: "New GPS item", content: "Save HSD?",
        data: { proposal_id: ID, rows, question: { question_id: ID, text: "Save this?", answer_type: "choice", options: [{ id: "confirm", label: "Confirm" }, { id: "cancel", label: "Cancel" }] } },
    });
    const copy: MessagePart = { type: "text", title: "", content: "copy", data: { fallback_for: ID } };
    const stored = (parts: MessagePart[], trace: StoredMessage["trace"] = { open: [ID] }, id = "7-turn0-assistant"): StoredMessage =>
        ({ id, version: "2.0", role: "assistant", turn_index: 0, created: 1, parts, trace, memory: {} });

    // a broken copy filter, or a builder that drops the content MessageReview reads
    const ok = mapStoredMessages([stored([card(["HSD"]), copy])]);
    console.assert(ok.messages.length === 1 && ok.messages[0].type === "action_card" && ok.messages[0].content === "Save HSD?", "a valid card gives one row and hides its copy");
    console.assert(ok.openQuestionIds.length === 1 && ok.openQuestionIds[0] === ID && ok.problems.length === 0, "open comes from trace.open");

    // a filter that hides every fallback_for text: a message stored without agent_parts has only
    // the copies, and a hidden copy would leave it blank
    const orphan = mapStoredMessages([stored([{ type: "text", title: "", content: "caption", data: null }, copy], null)]);
    console.assert(orphan.messages.length === 2 && orphan.messages[1].content === "copy", "a copy with no part before it is drawn");

    // a parser that accepts bad shapes, or a filter that hides the copy of a dropped part
    const bad = mapStoredMessages([stored([card(Array(9).fill("r")), copy])]);
    console.assert(bad.messages.length === 1 && bad.messages[0].type === "text" && bad.problems.length === 1 && bad.problems[0].kind === "malformed", "a card with 9 rows is dropped and its copy shows");

    // lost citations, lost remap, empty text, unknown part
    const cit: Citation = { id: "c", statement: "s", title: "t", authors: "a", year: 2020 };
    const mixed = mapStoredMessages([stored([
        { type: "text", title: "", content: "hi", data: null, citations: [cit] },
        { type: "summary_v2", title: "", content: "", data: { x: 1 } },
        { type: "text", title: "", content: "", data: null },
        { type: "weird" as MessagePart["type"], title: "", content: "", data: null },
    ], null)]);
    console.assert(mixed.messages.length === 2 && mixed.messages[0].metadata.citations?.length === 1, "a text part keeps its citation");
    console.assert(mixed.messages[1].type === "summary", "summary_v2 remaps to summary");
    console.assert(mixed.problems.length === 1 && mixed.problems[0].kind === "unknown" && mixed.openQuestionIds.length === 0, "an unknown part is one problem; a null trace gives no open ids and no problem");

    const badOpen = mapStoredMessages([stored([], { open: ["nope"] })]);
    console.assert(badOpen.openQuestionIds.length === 0 && badOpen.problems.length === 1 && badOpen.problems[0].kind === "open", "a malformed open gives [] and one problem");

    // a map that keys result rows and kills the retry
    const two = mapStoredMessages([
        stored([card([])], { open: [ID] }, "7-turn0-assistant"),
        stored([{ type: "action_result", title: "", content: "", data: { proposal_id: ID, status: "failed", message: "busy" } }], { open: [ID] }, "7-turn1-assistant"),
    ]);
    console.assert(latestAgentRowById(two.messages).get(ID)?.type === "action_card", "a failed result does not replace the card in the map");
    // a map that keeps the first row instead of the latest
    const again = mapStoredMessages([
        stored([card([])], { open: [ID] }, "7-turn0-assistant"),
        stored([{ type: "question", title: "", content: "", data: { question_id: ID, text: "Again?", answer_type: "text" } }], { open: [ID] }, "7-turn1-assistant"),
    ]);
    console.assert(latestAgentRowById(again.messages).get(ID)?.type === "question", "a later question row with the same id replaces the card");

    // a reply_click put on the wrong user row, or a pick that goes to the latest row of the id
    // instead of the row the coach clicked
    const user = (id: string): StoredMessage =>
        ({ id, version: "2.0", role: "user", turn_index: 0, created: 1, parts: [{ type: "text", title: "", content: "hi", data: null }], trace: null, memory: {} });
    const yesNo = { question_id: ID, text: "Chart it?", answer_type: "choice", options: [{ id: "yes", label: "Yes" }, { id: "no", label: "No" }] };
    const picked = mapStoredMessages([
        user("u0"), stored([card([])], { open: [ID] }, "7-turn0-assistant"),
        user("u1"), stored([{ type: "question", title: "", content: "", data: yesNo }], { open: [ID], reply_click: { question_id: ID, choices: ["confirm"] } }, "7-turn1-assistant"),
        user("u2"), stored([], { open: [], reply_click: { question_id: ID, choices: ["yes"] } }, "7-turn2-assistant"),
        user("u3"), stored([], { open: [], reply_click: { question_id: ID, choices: [] } }, "7-turn3-assistant"),
    ]);
    const picks = pickedChoicesByRow(picked.messages);
    const rowOf = (type: string) => picked.messages.find(m => m.type === type)?.id ?? "";
    console.assert(picks.get(rowOf("action_card"))?.[0] === "confirm" && picks.get(rowOf("question"))?.[0] === "yes" && picks.size === 2, "each click marks the row it answered; a typed answer marks none");
    console.assert(mapStoredMessages([stored([], { reply_click: { question_id: "x", choices: [] } })]).problems[0]?.kind === "reply_click", "a malformed reply_click is one problem");
    console.assert(parseOpenList([ID, ID2])?.length === 2 && parseOpenList([ID, "x"]) === null && parseOpenList(undefined) === null, "parseOpenList");
}

export interface ConversationGroup {
    label: string;
    items: ConversationSummary[];
}

const parseConvDate = (created: number | string) =>
    typeof created === "number" ? dayjs.unix(created) : dayjs(created);

export const groupConversationsByDate = (conversations: ConversationSummary[]): ConversationGroup[] => {
    const today = dayjs().startOf("day");
    const yesterday = today.subtract(1, "day");
    const sevenDaysAgo = today.subtract(7, "day");

    const buckets: { label: string; items: ConversationGroup["items"] }[] = [
        { label: "Today", items: [] },
        { label: "Yesterday", items: [] },
        { label: "Last 7 days", items: [] },
        { label: "Older", items: [] },
    ];

    for (const conv of conversations) {
        const d = parseConvDate(conv.created);
        if (d.isAfter(today)) buckets[0].items.push(conv);
        else if (d.isAfter(yesterday)) buckets[1].items.push(conv);
        else if (d.isAfter(sevenDaysAgo)) buckets[2].items.push(conv);
        else buckets[3].items.push(conv);
    }

    return buckets.filter(b => b.items.length > 0);
};

export const formatConversationDate = (created: number | string): string => {
    const d = parseConvDate(created);
    const today = dayjs().startOf("day");
    const yesterday = today.subtract(1, "day");
    if (d.isAfter(today)) return `Today, ${d.format("h:mm A")}`;
    if (d.isAfter(yesterday)) return `Yesterday, ${d.format("h:mm A")}`;
    return d.format("MMM D, h:mm A");
};

// A reader who asks the system for less motion gets the text at once, not typed out. Used by the
// artifact reveal and by the reasoning drip, so it lives here rather than in either of them.
export const prefersReducedMotion = (): boolean =>
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
